import React from "react";
import { Clock, Sparkles, Info } from "lucide-react";
import type { LifeRecord } from "@/lib/infiniteSelves";
export interface DayInLifeTabProps {
  life: LifeRecord | null;
  cardHue?: number;
}
export default function DayInLifeTab({
  life,
  cardHue = 186
}: DayInLifeTabProps): JSX.Element {
  if (!life) {
    return <div className="space-y-6">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border text-xs text-muted-foreground font-sans">
          <Info className="size-4 text-primary shrink-0" />
          <span>This chapter is not available for this result. Generate again to see it.</span>
        </div>
        <div className="p-4 sm:p-5 rounded-xl bg-card/65 border border-border/70 space-y-2">
          <span className="text-xs font-mono uppercase tracking-widest text-primary font-bold block">
            A Day in This Life · 8 Chronological Beats
          </span>
          <p className="text-xs text-muted-foreground leading-relaxed font-sans">
            Follows the subject's waking hours, workplace environment, interactions with named companions, pivotal daily tasks, and twilight reflections, coherent with the unique physics and culture of this world.
          </p>
        </div>
      </div>;
  }
  const {
    dayInLife
  } = life;
  if (!dayInLife) return <></>;
  return <div className="space-y-6">
      {/* Intro Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-3">
        <div className="space-y-0.5">
          <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
            <Clock className="size-4 text-primary shrink-0" />
            <span>{dayInLife.title || "A Day in This Life"}</span>
          </h3>
          <p className="text-xs text-muted-foreground font-sans">
            An eight-beat chronicle through an authentic daily cycle in this reality.
          </p>
        </div>
        <span className="text-[10px] font-mono text-primary border border-primary/30 bg-primary/10 px-2.5 py-0.5 rounded-full shrink-0 self-start sm:self-auto">
          {dayInLife.beats?.length ?? 8} Beats
        </span>
      </div>

      {/* Vertical Sequence of 8 Beats */}
      <div className="relative pl-6 sm:pl-8 space-y-6 before:absolute before:top-3 before:bottom-3 before:left-2.5 sm:before:left-3 before:w-0.5 before:bg-gradient-to-b before:from-primary/60 before:via-border before:to-primary/20">
        {dayInLife.beats?.map((beat, idx) => <div key={idx} className="relative space-y-1.5 group">
            {/* Timeline Node */}
            <span className="absolute -left-6 sm:-left-8 top-1.5 size-3 sm:size-3.5 rounded-full border-2 border-background shadow-[0_0_8px_var(--glow-primary)] transition-transform group-hover:scale-125" style={{
          backgroundColor: `hsl(${cardHue} 100% 55%)`
        }} aria-hidden="true" />

            <div className="p-4 sm:p-5 rounded-xl bg-card border border-border/80 shadow-sm space-y-2 hover:border-primary/40 transition-colors">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/40 pb-2">
                <span className="font-mono text-xs font-bold uppercase tracking-wider text-primary">
                  {beat.time}
                </span>
                <span className="font-serif font-bold text-sm sm:text-base text-foreground">
                  {beat.heading}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-foreground/90 font-sans leading-relaxed">
                {beat.text}
              </p>
            </div>
          </div>)}
      </div>

      {/* Day In Life Closing Statement */}
      {dayInLife.closing && <div className="p-4 sm:p-5 rounded-xl bg-card/90 border border-primary/30 shadow-md space-y-1.5">
          <span className="text-[10px] font-mono uppercase tracking-widest text-primary font-bold flex items-center gap-1.5">
            <Sparkles className="size-3 text-primary shrink-0" />
            <span>Evening Reflection</span>
          </span>
          <p className="text-xs sm:text-sm text-foreground/95 italic font-sans leading-relaxed">
            "{dayInLife.closing}"
          </p>
        </div>}
    </div>;
}